import { useState, useRef, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChatBubble } from "@/components/ai/ChatBubble";
import { Sparkles, Send, Loader2, Info } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
    "What are my largest holdings?",
    "How diversified am I?",
    "What percentage of my portfolio is technology?",
    "Which holdings contributed most to my gains?",
    "How is my portfolio allocated across sub-portfolios?",
];

export function ChatTab() {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [session, setSession] = useState(null);
    const [tool, setTool] = useState(null);
    const endRef = useRef(null);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

    const send = async (text) => {
        const msg = (text || input).trim();
        if (!msg || loading) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
        setLoading(true);
        setTool(null);
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat/stream`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message: msg, session_id: session }),
            });
            if (!res.ok || !res.body) throw new Error("stream failed");
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            const appendDelta = (c) => setMessages((m) => {
                const arr = [...m]; const last = arr[arr.length - 1];
                arr[arr.length - 1] = { ...last, text: last.text + c }; return arr;
            });
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop();
                for (const part of parts) {
                    const line = part.replace(/^data:\s?/, "").trim();
                    if (!line) continue;
                    let ev; try { ev = JSON.parse(line); } catch { continue; }
                    if (ev.t === "session") setSession(ev.session_id);
                    else if (ev.t === "delta") { setTool(null); appendDelta(ev.c); }
                    else if (ev.t === "tool") setTool(ev.name);
                    else if (ev.t === "done") setTool(null);
                    else if (ev.t === "error") appendDelta("\n\n_Sorry, I ran into an error._");
                }
            }
        } catch (e) {
            toast.error(apiErr(e));
            setMessages((m) => {
                const arr = [...m]; const last = arr[arr.length - 1];
                if (last?.role === "assistant" && !last.text) arr[arr.length - 1] = { ...last, text: "Sorry, I couldn't process that request." };
                return arr;
            });
        } finally { setLoading(false); setTool(null); }
    };

    const lastMsg = messages[messages.length - 1];
    const showStatus = loading && (tool || (lastMsg?.role === "assistant" && !lastMsg?.text));

    return (
        <Card className="flex flex-col h-[calc(100vh-16rem)] min-h-[480px]">
            <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-messages">
                {messages.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center text-center">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-3"><Sparkles className="w-6 h-6 text-primary" /></div>
                        <h3 className="font-heading font-semibold">Ask your AI Portfolio Analyst</h3>
                        <p className="text-sm text-muted-foreground mt-1 max-w-sm">Grounded in your live portfolio data. Try one of these:</p>
                        <div className="flex flex-wrap gap-2 justify-center mt-4 max-w-lg">
                            {SUGGESTIONS.map((s) => (
                                <button key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-muted transition-colors" data-testid="chat-suggestion">{s}</button>
                            ))}
                        </div>
                    </div>
                )}
                {messages.map((m, i) => (m.role === "assistant" && !m.text) ? null : <ChatBubble key={i} role={m.role} text={m.text} />)}
                {showStatus && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="chat-status">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {tool ? `Reading ${tool.replace(/_/g, " ").replace("get ", "")}…` : "Analyzing your portfolio…"}
                    </div>
                )}
                <div ref={endRef} />
            </div>
            <div className="border-t border-border p-3">
                <div className="flex gap-2">
                    <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                        placeholder="Ask about your portfolio…" data-testid="ai-chat-input" disabled={loading} />
                    <Button onClick={() => send()} disabled={loading || !input.trim()} data-testid="ai-chat-send"><Send className="w-4 h-4" /></Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1"><Info className="w-3 h-3" />Responses are AI-generated and not financial advice.</p>
            </div>
        </Card>
    );
}
